import { supabaseClient } from "supabase";

/**
 * The Applications queue: Jotform submissions and the agenda imports
 * (pa-import-events), the rows a curator converts by hand. Facebook imports
 * are not listed here: the curators do not work them yet, and the newsroom
 * desk will be their home (2026-10-31, owner ruling). The rows stay in the
 * table, untouched; they are only no longer listed. The same predicate keeps
 * the editor's candidates off this queue (Phase 31 D-13): an editor row always
 * carries a source (`facebook`, `paste`), and those rows are worked on the
 * Desk.
 *
 * `source IS NULL` is the Jotform/import predicate, not `source <> 'facebook'`.
 * A Jotform submission has no source at all — that is what exempts it from
 * the provenance unique index — and `.eq()`/`.neq()` never match null in
 * PostgREST.
 */
export type UseQueryApplicationsParams = {
  page: number;
  perPage: number;
};

export const queryApplications = async ({
  page,
  perPage,
}: UseQueryApplicationsParams) => {
  // `count: "exact"` is load-bearing: PostgREST silently truncates a select at
  // 1000 rows, so the total can never be read off the fetched array. Keep it,
  // and keep the `.range()` that pairs with it.
  const query = supabaseClient
    .from("applications")
    .select("*", { count: "exact" })
    .eq("processed", false)
    .is("source", null)
    // Rows an importer discarded never belong in the working queue. It is
    // `import_status IS NULL OR import_status <> 'discarded'`, not a bare
    // not-equal predicate: a Jotform submission has a null import_status, and
    // SQL `NULL <> 'discarded'` is not true, so a bare neq would silently drop
    // every Jotform row. The string is a constant, never built from input.
    .or("import_status.is.null,import_status.neq.discarded");

  // Newest first: the latest submissions and imports are at the top.
  const paged = query
    .order("created_at", { ascending: false })
    .range((page - 1) * perPage, (page - 1) * perPage + perPage - 1);

  const { data, count, error } = await paged;

  if (error) {
    throw error;
  }
  return { applications: data || [], count };
};
